/**
 * 创作智能体展示组件
 *
 * 展示三大核心能力：智能体、工作流、Agent Teams
 * 左右交替布局：智能体(左图右文)、工作流(左文右图)、多智能体(左图右文)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { homePageContent } from './homePageContent';

// ============ 工具卡片演示组件（简化版） ============
interface DemoToolCardProps {
  icon: React.ReactNode;
  iconColor: string;
  label: string;
  summary?: React.ReactNode;
  delay?: number;
}

const DemoToolCard: React.FC<DemoToolCardProps> = ({
  icon,
  iconColor,
  label,
  summary,
  delay = 0,
}) => {
  const [visible, setVisible] = useState(false);
  const [cardStatus, setCardStatus] = useState<'detected' | 'input-available' | 'output-available'>('detected');

  useEffect(() => {
    setVisible(false);
    setCardStatus('detected');
    const t1 = setTimeout(() => setVisible(true), delay);
    const t2 = setTimeout(() => setCardStatus('input-available'), delay + 600);
    const t3 = setTimeout(() => setCardStatus('output-available'), delay + 1800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [delay]);

  const statusConfig = {
    detected: {
      label: '已检测',
      icon: (
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'text-gray-400',
      border: 'border-gray-200/50 bg-gray-50/50',
    },
    'input-available': {
      label: '执行中...',
      icon: (
        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      ),
      color: 'text-paper-600',
      border: 'border-paper-300/50 bg-paper-50/30',
    },
    'output-available': {
      label: '已完成',
      icon: (
        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      color: 'text-green-500',
      border: 'border-green-300/50 bg-green-50/30',
    },
  };

  const config = statusConfig[cardStatus];

  return (
    <div
      className={`rounded-lg border transition-all duration-500 overflow-hidden ${config.border} ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
      }`}
    >
      {/* 头部 */}
      <div className="flex items-center gap-2 px-3 py-2">
        <span className={iconColor}>{icon}</span>
        <span className="text-[11px] font-medium text-gray-700 flex-1">{label}</span>
        <span className={`flex items-center gap-1 text-[10px] ${config.color}`}>
          {config.icon}
          {config.label}
        </span>
      </div>
      {/* 摘要内容 */}
      {summary && cardStatus !== 'detected' && (
        <div className="px-3 pb-2 transition-all duration-500">
          {summary}
        </div>
      )}
    </div>
  );
};

// ============ 智能体聊天演示组件 ============
type AnimationPhase = 'idle' | 'user-msg' | 'ai-thinking' | 'tool-setting' | 'tool-chapter' | 'ai-reply' | 'reset';

const AgentChatDemo: React.FC = () => {
  const [phase, setPhase] = useState<AnimationPhase>('idle');
  const [userText, setUserText] = useState('');
  const [aiText, setAiText] = useState('');
  const [aiReplyText, setAiReplyText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const runAnimation = useCallback(() => {
    clearAllTimeouts();
    setPhase('idle');
    setUserText('');
    setAiText('');
    setAiReplyText('');

    // 阶段 1: 用户消息打字
    const t1 = setTimeout(() => {
      setPhase('user-msg');
      const text = '帮我写一段仙侠小说开头';
      let idx = 0;
      const interval = setInterval(() => {
        idx += 1;
        setUserText(text.slice(0, idx));
        if (idx >= text.length) clearInterval(interval);
      }, 45);
      timeoutsRef.current.push(interval as unknown as ReturnType<typeof setTimeout>);
    }, 300);
    timeoutsRef.current.push(t1);

    // 阶段 2: AI 思考中
    const t2 = setTimeout(() => {
      setPhase('ai-thinking');
      const text = '好的，我来为你创作……';
      let idx = 0;
      const interval = setInterval(() => {
        idx += 1;
        setAiText(text.slice(0, idx));
        if (idx >= text.length) clearInterval(interval);
      }, 50);
      timeoutsRef.current.push(interval as unknown as ReturnType<typeof setTimeout>);
    }, 1400);
    timeoutsRef.current.push(t2);

    // 阶段 3: 读取设定工具卡片出现
    const t3 = setTimeout(() => {
      setPhase('tool-setting');
    }, 2800);
    timeoutsRef.current.push(t3);

    // 阶段 4: 章节撰写工具卡片出现
    const t4 = setTimeout(() => {
      setPhase('tool-chapter');
    }, 4200);
    timeoutsRef.current.push(t4);

    // 阶段 5: AI 最终回复
    const t5 = setTimeout(() => {
      setPhase('ai-reply');
      const text = '青冥浩荡不见底，日月照耀金银台……';
      let idx = 0;
      const interval = setInterval(() => {
        idx += 1;
        setAiReplyText(text.slice(0, idx));
        if (idx >= text.length) clearInterval(interval);
      }, 55);
      timeoutsRef.current.push(interval as unknown as ReturnType<typeof setTimeout>);
    }, 6200);
    timeoutsRef.current.push(t5);

    // 重置循环
    const t6 = setTimeout(() => {
      setPhase('reset');
      const t7 = setTimeout(() => runAnimation(), 400);
      timeoutsRef.current.push(t7);
    }, 9500);
    timeoutsRef.current.push(t6);
  }, [clearAllTimeouts]);

  useEffect(() => {
    const initial = setTimeout(() => runAnimation(), 500);
    return () => {
      clearTimeout(initial);
      clearAllTimeouts();
    };
  }, [runAnimation, clearAllTimeouts]);

  // 光标闪烁
  useEffect(() => {
    const interval = setInterval(() => setShowCursor(prev => !prev), 530);
    return () => clearInterval(interval);
  }, []);

  const isToolPhase = phase === 'tool-setting' || phase === 'tool-chapter' || phase === 'ai-reply' || phase === 'reset';
  const isChapterToolPhase = phase === 'tool-chapter' || phase === 'ai-reply' || phase === 'reset';
  const isAiThinkingOrLater = phase === 'ai-thinking' || isToolPhase;

  return (
    <div className="w-full h-full bg-gray-50 rounded-lg p-3 flex flex-col gap-2 overflow-hidden">
      {/* 模拟窗口标题栏 */}
      <div className="flex items-center gap-1.5 mb-1 shrink-0">
        <div className="w-2 h-2 rounded-full bg-red-400" />
        <div className="w-2 h-2 rounded-full bg-yellow-400" />
        <div className="w-2 h-2 rounded-full bg-green-400" />
        <span className="ml-2 text-[10px] text-gray-400">智能体 · 施耐庵</span>
      </div>

      {/* 聊天内容区域 */}
      <div className="flex-1 flex flex-col gap-2 overflow-hidden">
        {/* 用户消息 */}
        <div
          className={`flex justify-end transition-all duration-500 ${
            phase !== 'idle' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
          }`}
        >
          <div className="bg-ink-800 text-white text-[11px] px-3 py-1.5 rounded-xl rounded-tr-sm max-w-[90%]">
            {userText}
            {phase === 'user-msg' && (
              <span className={`inline-block w-1.5 h-3 ml-0.5 align-middle ${showCursor ? 'bg-white/70' : 'bg-transparent'}`} />
            )}
          </div>
        </div>

        {/* AI 思考中 */}
        <div
          className={`flex justify-start transition-all duration-500 ${
            isAiThinkingOrLater ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
          }`}
        >
          <div className="flex gap-2 max-w-[95%]">
            {/* 头像 */}
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-paper-500 to-paper-700 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[9px] text-white font-bold">施</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="bg-white border border-paper-200 text-ink-700 text-[11px] px-3 py-1.5 rounded-xl rounded-tl-sm shadow-sm">
                {aiText}
                {(phase === 'ai-thinking' || phase === 'tool-setting') && (
                  <span className={`inline-block w-1.5 h-3 ml-0.5 align-middle ${showCursor ? 'bg-paper-400' : 'bg-transparent'}`} />
                )}
              </div>
              {/* 工具卡片区域 */}
              <div className="flex flex-col gap-1.5">
                {/* 读取设定工具 */}
                <DemoToolCard
                  icon={
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  }
                  iconColor="text-blue-500"
                  label="读取设定"
                  delay={isToolPhase ? 0 : 99999}
                  summary={
                    <div className="text-[10px] text-gray-500 truncate">世界观: 仙侠修真</div>
                  }
                />

                {/* 章节撰写工具 */}
                <DemoToolCard
                  icon={
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  }
                  iconColor="text-amber-500"
                  label="章节撰写"
                  delay={isChapterToolPhase ? 200 : 99999}
                  summary={
                    <div className="text-[10px] text-gray-500 truncate">第1章 · 初入仙门</div>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* AI 最终回复 */}
        <div
          className={`flex justify-start transition-all duration-500 ${
            phase === 'ai-reply' || phase === 'reset' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
          }`}
        >
          <div className="flex gap-2 max-w-[95%]">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-paper-500 to-paper-700 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-[9px] text-white font-bold">施</span>
            </div>
            <div className="bg-white border border-paper-200 text-ink-700 text-[11px] px-3 py-1.5 rounded-xl rounded-tl-sm shadow-sm">
              {aiReplyText}
              {phase === 'ai-reply' && (
                <span className={`inline-block w-1.5 h-3 ml-0.5 align-middle ${showCursor ? 'bg-paper-400' : 'bg-transparent'}`} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ 工作流演示组件 (n8n 风格) ============
type WorkflowPhase = 'idle' | 'start' | 'conceive' | 'write1' | 'proofread1' | 'rewrite' | 'write2' | 'proofread2' | 'end';

// 候选机器人图标方案 — 从机器人图标参考图中提取（颜色改为当前主题的深灰色系）

// 方案A: 耳罩机器人（参考第4行第1个）
const RobotIconV1: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <rect x="5" y="7" width="14" height="13" rx="4" />
    <path d="M5 12H3a1 1 0 000 2h2" />
    <path d="M19 12h2a1 1 0 000-2h-2" />
    <circle cx="9" cy="13" r="1.2" />
    <circle cx="15" cy="13" r="1.2" />
    <path d="M10 16.5h4" />
    <path d="M9 3h6" />
    <circle cx="12" cy="3" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

// 方案B: 双天线机器人（参考第4行第3个）
// const RobotIconV2: React.FC<{ size?: number }> = ({ size = 24 }) => (
//   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
//     <rect x="4" y="8" width="16" height="12" rx="5" />
//     <path d="M8 4v4" />
//     <path d="M16 4v4" />
//     <circle cx="8" cy="4" r="1.5" fill="currentColor" stroke="none" />
//     <circle cx="16" cy="4" r="1.5" fill="currentColor" stroke="none" />
//     <circle cx="9" cy="14" r="1.2" />
//     <circle cx="15" cy="14" r="1.2" />
//     <path d="M10 17h4" />
//   </svg>
// );

const RobotIcon = RobotIconV1;

const TriggerIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="currentColor" stroke="none" opacity="0.9" />
  </svg>
);

const DoneIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12l2.5 2.5L16 9" />
  </svg>
);

const WorkflowDemo: React.FC = () => {
  const [phase, setPhase] = useState<WorkflowPhase>('idle');
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const runAnimation = useCallback(() => {
    clearAllTimeouts();
    setPhase('idle');

    const delays: [WorkflowPhase, number][] = [
      ['start', 400],
      ['conceive', 1500],
      ['write1', 3000],
      ['proofread1', 4500],
      ['rewrite', 6000],
      ['write2', 7500],
      ['proofread2', 9000],
      ['end', 10500],
    ];

    let acc = 0;
    delays.forEach(([p, delay]) => {
      acc = delay;
      const t = setTimeout(() => setPhase(p), acc);
      timeoutsRef.current.push(t);
    });

    const tReset = setTimeout(() => {
      setPhase('idle');
      const tLoop = setTimeout(() => runAnimation(), 800);
      timeoutsRef.current.push(tLoop);
    }, 12000);
    timeoutsRef.current.push(tReset);
  }, [clearAllTimeouts]);

  useEffect(() => {
    const initial = setTimeout(() => runAnimation(), 500);
    return () => {
      clearTimeout(initial);
      clearAllTimeouts();
    };
  }, [runAnimation, clearAllTimeouts]);

  const canvasW = 420;
  const canvasH = 210;
  const iconSize = 24;
  const halfIcon = iconSize / 2;
  const row1Y = 55;
  const row2Y = 155;

  const nodes = [
    { id: 'start', label: '开始', cx: 55, cy: row1Y, type: 'trigger' as const, color: '#f59e0b' },
    { id: 'conceive', label: '构思', cx: 160, cy: row1Y, type: 'agent' as const, color: '#3b82f6' },
    { id: 'write', label: '写作', cx: 265, cy: row1Y, type: 'agent' as const, color: '#6366f1' },
    { id: 'proofread', label: '校对', cx: 265, cy: row2Y, type: 'agent' as const, color: '#8b5cf6' },
    { id: 'end', label: '结束', cx: 160, cy: row2Y, type: 'end' as const, color: '#10b981' },
  ];

  const getNodeById = (id: string) => nodes.find(n => n.id === id)!;

  const isCurrentNode = (nodeId: string) => {
    if (nodeId === 'write') return phase === 'write1' || phase === 'write2' || phase === 'rewrite';
    if (nodeId === 'proofread') return phase === 'proofread1' || phase === 'proofread2';
    return phase === nodeId;
  };

  type ConnDef = { id: string; from: string; to: string; label?: string; type: 'forward' | 'down' | 'return' | 'left' };
  const connections: ConnDef[] = [
    { id: 'c1', from: 'start', to: 'conceive', type: 'forward' },
    { id: 'c2', from: 'conceive', to: 'write', type: 'forward' },
    { id: 'c3', from: 'write', to: 'proofread', type: 'down' },
    { id: 'c4', from: 'proofread', to: 'write', label: '未通过', type: 'return' },
    { id: 'c5', from: 'proofread', to: 'end', label: '通过', type: 'left' },
  ];

  const isConnectionActive = (conn: ConnDef) => {
    switch (conn.id) {
      case 'c1': return phase === 'start';
      case 'c2': return phase === 'conceive';
      case 'c3': return phase === 'write1' || phase === 'write2';
      case 'c4': return phase === 'rewrite';
      case 'c5': return phase === 'proofread2';
      default: return false;
    }
  };

  const getConnectionPath = (conn: ConnDef) => {
    const from = getNodeById(conn.from);
    const to = getNodeById(conn.to);

    if (conn.type === 'forward') {
      const x1 = from.cx + halfIcon + 6;
      const x2 = to.cx - halfIcon - 6;
      const y = from.cy;
      return `M ${x1} ${y} L ${x2} ${y}`;
    }

    if (conn.type === 'down') {
      const x = from.cx;
      const y1 = from.cy + halfIcon + 6;
      const y2 = to.cy - halfIcon - 6;
      return `M ${x} ${y1} L ${x} ${y2}`;
    }

    if (conn.type === 'return') {
      const startX = from.cx + halfIcon + 6;
      const startY = from.cy;
      const endX = to.cx + halfIcon + 6;
      const endY = to.cy;
      const loopX = Math.max(startX, endX) + 28;
      return `M ${startX} ${startY} L ${loopX} ${startY} Q ${loopX + 8} ${startY}, ${loopX + 8} ${startY - 8} L ${loopX + 8} ${endY + 8} Q ${loopX + 8} ${endY}, ${loopX} ${endY} L ${endX} ${endY}`;
    }

    if (conn.type === 'left') {
      const x1 = from.cx - halfIcon - 6;
      const x2 = to.cx + halfIcon + 6;
      const y = from.cy;
      return `M ${x1} ${y} L ${x2} ${y}`;
    }

    return '';
  };

  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'trigger': return <TriggerIcon size={iconSize} />;
      case 'agent': return <RobotIcon size={iconSize} />;
      case 'end': return <DoneIcon size={iconSize} />;
      default: return null;
    }
  };

  return (
    <div className="w-full h-full bg-white rounded-lg relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage: 'radial-gradient(circle, #9ca3af 1px, transparent 1px)',
          backgroundSize: '16px 16px',
        }}
      />

      <svg
        className="absolute inset-0"
        viewBox={`0 0 ${canvasW} ${canvasH}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <marker id="wf-arrow" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
            <path d="M0,0 L7,2.5 L0,5" context-stroke="fill" />
          </marker>
        </defs>

        {connections.map(conn => {
          const active = isConnectionActive(conn);
          const pathD = getConnectionPath(conn);
          const fromNode = getNodeById(conn.from);

          const inactiveColor = '#e5e7eb';
          const activeColor = fromNode.color;
          const lineColor = active ? activeColor : inactiveColor;

          return (
            <g key={conn.id}>
              <path
                id={`path-${conn.id}`}
                d={pathD}
                fill="none"
                stroke={lineColor}
                strokeWidth={active ? 2 : 1.5}
                markerEnd="url(#wf-arrow)"
                className="transition-all duration-500"
              />
              {active && (
                <>
                  <circle r="2.5" fill={activeColor}>
                    <animateMotion dur="1s" repeatCount="indefinite">
                      <mpath href={`#path-${conn.id}`} />
                    </animateMotion>
                  </circle>
                  <circle r="2.5" fill={activeColor} opacity="0.4">
                    <animateMotion dur="1s" repeatCount="indefinite" begin="0.33s">
                      <mpath href={`#path-${conn.id}`} />
                    </animateMotion>
                  </circle>
                  <circle r="2" fill={activeColor} opacity="0.2">
                    <animateMotion dur="1s" repeatCount="indefinite" begin="0.66s">
                      <mpath href={`#path-${conn.id}`} />
                    </animateMotion>
                  </circle>
                </>
              )}
              {conn.label && (
                <text
                  x={conn.type === 'return'
                    ? Math.max(getNodeById(conn.from).cx, getNodeById(conn.to).cx) + halfIcon + 38
                    : (getNodeById(conn.from).cx + getNodeById(conn.to).cx) / 2}
                  y={conn.type === 'return'
                    ? (getNodeById(conn.from).cy + getNodeById(conn.to).cy) / 2 + 1
                    : conn.type === 'down'
                      ? getNodeById(conn.from).cy + (getNodeById(conn.to).cy - getNodeById(conn.from).cy) / 2
                      : getNodeById(conn.from).cy - 10}
                  className="text-[8px] font-medium"
                  fill={lineColor}
                  textAnchor="middle"
                  dominantBaseline="middle"
                >
                  {conn.label}
                </text>
              )}
            </g>
          );
        })}

        {nodes.map(node => {
          const current = isCurrentNode(node.id);

          return (
            <g key={node.id} className="transition-all duration-500">
              {current && (
                <circle
                  cx={node.cx}
                  cy={node.cy}
                  r={halfIcon + 5}
                  fill="none"
                  stroke={node.color}
                  strokeWidth="0.8"
                  strokeDasharray="3 2"
                  opacity="0.5"
                >
                  <animate attributeName="stroke-dashoffset" from="0" to="10" dur="1.5s" repeatCount="indefinite" />
                </circle>
              )}
              <foreignObject
                x={node.cx - halfIcon}
                y={node.cy - halfIcon}
                width={iconSize}
                height={iconSize}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  color: node.color,
                }}>
                  {getNodeIcon(node.type)}
                </div>
              </foreignObject>
              <text
                x={node.cx}
                y={node.cy + halfIcon + 14}
                className="text-[10px] font-medium"
                fill={node.color}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {node.label}
              </text>
            </g>
          );
        })}
      </svg>

      <div className="absolute top-2.5 left-3">
        <div className={`text-[9px] font-medium px-2 py-0.5 rounded transition-all duration-150 ${
          phase === 'idle'
            ? 'bg-gray-50 text-gray-400'
            : 'bg-gray-100 text-gray-500'
        }`}>
          {phase === 'idle' && '准备就绪'}
          {phase === 'start' && '启动工作流...'}
          {phase === 'conceive' && '构思中...'}
          {(phase === 'write1' || phase === 'write2') && '写作中...'}
          {(phase === 'proofread1' || phase === 'proofread2') && '校对中...'}
          {phase === 'rewrite' && '未通过，重新写作...'}
          {phase === 'end' && '已完成'}
        </div>
      </div>
    </div>
  );
};

// ============ Agent Teams 演示组件（高端科技风格） ============
type TeamPhase = 'idle' | 'user-msg' | 'assistant-reply1' | 'subagent-cards' | 'experts-done' | 'writer-done' | 'reviewer-done' | 'assistant-reply2' | 'reset';

interface AgentNodeProps {
  name: string;
  status: 'pending' | 'running' | 'done';
  delay: number;
  color: string;
  hints: string[];
}

const AgentNode: React.FC<AgentNodeProps> = ({ name, status, delay, color, hints }) => {
  const [visible, setVisible] = useState(false);
  const [hintIndex, setHintIndex] = useState(0);

  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  useEffect(() => {
    if (status !== 'running') return;
    setHintIndex(0);
    const interval = setInterval(() => {
      setHintIndex(prev => (prev + 1) % hints.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [status, hints.length]);

  const statusConfig = {
    pending: {
      label: '待执行',
      glow: '',
      iconOpacity: 'opacity-30',
    },
    running: {
      label: '执行中',
      glow: `shadow-[0_0_12px_${color}40]`,
      iconOpacity: 'opacity-100',
    },
    done: {
      label: '已完成',
      glow: `shadow-[0_0_8px_${color}30]`,
      iconOpacity: 'opacity-100',
    },
  };

  const s = statusConfig[status];

  return (
    <div
      className={`flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-all duration-700 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'
      }`}
      style={{
        background: status === 'pending' ? 'rgba(30,41,59,0.4)' : 'rgba(30,41,59,0.7)',
        border: `1px solid ${status !== 'pending' ? color + '30' : 'rgba(71,85,105,0.3)'}`,
        boxShadow: status !== 'pending' ? `0 0 20px ${color}10` : 'none',
      }}
    >
      <div
        className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 transition-all duration-500 ${s.iconOpacity} ${s.glow}`}
        style={{
          background: `linear-gradient(135deg, ${color}20, ${color}40)`,
          border: `1px solid ${color}50`,
        }}
      >
        <RobotIcon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span
            className="text-[10px] font-medium truncate"
            style={{ color: status === 'pending' ? '#64748b' : '#e2e8f0' }}
          >
            {name}
          </span>
          <span
            className="text-[8px] font-medium ml-2 shrink-0"
            style={{ color: status === 'pending' ? '#475569' : status === 'running' ? color : '#34d399' }}
          >
            {s.label}
          </span>
        </div>
        <div className="h-[2px] rounded-full bg-gray-700/60 overflow-hidden mb-0.5">
          <div
            className={`h-full rounded-full transition-all duration-1000 ${status === 'running' ? 'animate-pulse' : ''}`}
            style={{
              width: status === 'pending' ? '0%' : status === 'running' ? '65%' : '100%',
              background: status === 'pending' ? 'transparent' : `linear-gradient(90deg, ${color}, ${color}90)`,
              boxShadow: status !== 'pending' ? `0 0 6px ${color}60` : 'none',
            }}
          />
        </div>
        <div className="h-3 relative overflow-hidden">
          {status === 'running' && (
            <span
              key={hintIndex}
              className="text-[8px] font-mono absolute animate-[fadeInOut_1.8s_ease-in-out]"
              style={{ color: color + 'a0' }}
            >
              {hints[hintIndex]}
            </span>
          )}
          {status === 'done' && (
            <span className="text-[8px] font-mono" style={{ color: '#34d399' }}>
              ✓ {hints[hints.length - 1]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const AgentTeamsDemo: React.FC = () => {
  const [phase, setPhase] = useState<TeamPhase>('idle');
  const [userText, setUserText] = useState('');
  const [assistantText1, setAssistantText1] = useState('');
  const [assistantText2, setAssistantText2] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAllTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const runAnimation = useCallback(() => {
    clearAllTimeouts();
    setPhase('idle');
    setUserText('');
    setAssistantText1('');
    setAssistantText2('');

    const t1 = setTimeout(() => {
      setPhase('user-msg');
      const text = '帮我写一本克苏鲁+赛博朋克风小说……';
      let idx = 0;
      const interval = setInterval(() => {
        idx += 1;
        setUserText(text.slice(0, idx));
        if (idx >= text.length) clearInterval(interval);
      }, 50);
      timeoutsRef.current.push(interval as unknown as ReturnType<typeof setTimeout>);
    }, 400);
    timeoutsRef.current.push(t1);

    const t2 = setTimeout(() => {
      setPhase('assistant-reply1');
      const text = '真是个有趣的创意，让我启动多个专家完成这个任务……';
      let idx = 0;
      const interval = setInterval(() => {
        idx += 1;
        setAssistantText1(text.slice(0, idx));
        if (idx >= text.length) clearInterval(interval);
      }, 45);
      timeoutsRef.current.push(interval as unknown as ReturnType<typeof setTimeout>);
    }, 2000);
    timeoutsRef.current.push(t2);

    const t3 = setTimeout(() => {
      setPhase('subagent-cards');
    }, 4200);
    timeoutsRef.current.push(t3);

    const t4 = setTimeout(() => {
      setPhase('experts-done');
    }, 7000);
    timeoutsRef.current.push(t4);

    const t5 = setTimeout(() => {
      setPhase('writer-done');
    }, 9000);
    timeoutsRef.current.push(t5);

    const t5b = setTimeout(() => {
      setPhase('reviewer-done');
    }, 11000);
    timeoutsRef.current.push(t5b);

    const t6 = setTimeout(() => {
      setPhase('assistant-reply2');
      const text = '我已经完成克苏鲁+赛博朋克融合风创作……';
      let idx = 0;
      const interval = setInterval(() => {
        idx += 1;
        setAssistantText2(text.slice(0, idx));
        if (idx >= text.length) clearInterval(interval);
      }, 50);
      timeoutsRef.current.push(interval as unknown as ReturnType<typeof setTimeout>);
    }, 12500);
    timeoutsRef.current.push(t6);

    const t7 = setTimeout(() => {
      setPhase('reset');
      const t8 = setTimeout(() => runAnimation(), 600);
      timeoutsRef.current.push(t8);
    }, 15500);
    timeoutsRef.current.push(t7);
  }, [clearAllTimeouts]);

  useEffect(() => {
    const initial = setTimeout(() => runAnimation(), 500);
    return () => {
      clearTimeout(initial);
      clearAllTimeouts();
    };
  }, [runAnimation, clearAllTimeouts]);

  useEffect(() => {
    const interval = setInterval(() => setShowCursor(prev => !prev), 530);
    return () => clearInterval(interval);
  }, []);

  const showAssistant1 = phase === 'assistant-reply1' || phase === 'subagent-cards' || phase === 'experts-done' || phase === 'writer-done' || phase === 'reviewer-done' || phase === 'assistant-reply2' || phase === 'reset';
  const showCards = phase === 'subagent-cards' || phase === 'experts-done' || phase === 'writer-done' || phase === 'reviewer-done' || phase === 'assistant-reply2' || phase === 'reset';
  const showAssistant2 = phase === 'assistant-reply2' || phase === 'reset';

  const getExpertStatus = () => {
    if (phase === 'subagent-cards') return 'running';
    if (phase === 'experts-done' || phase === 'writer-done' || phase === 'reviewer-done' || phase === 'assistant-reply2' || phase === 'reset') return 'done';
    return 'pending';
  };

  const getWriterStatus = () => {
    if (phase === 'subagent-cards') return 'pending';
    if (phase === 'experts-done') return 'running';
    if (phase === 'writer-done' || phase === 'reviewer-done' || phase === 'assistant-reply2' || phase === 'reset') return 'done';
    return 'pending';
  };

  const getReviewerStatus = () => {
    if (phase === 'subagent-cards' || phase === 'experts-done' || phase === 'writer-done') return 'pending';
    if (phase === 'reviewer-done') return 'running';
    if (phase === 'assistant-reply2' || phase === 'reset') return 'done';
    return 'pending';
  };

  const agents = [
    { name: '克苏鲁专家', status: getExpertStatus, color: '#818cf8', hints: ['检索克苏鲁神话体系...', '分析旧日支配者特征...', '提取宇宙恐怖元素'] },
    { name: '赛博朋克专家', status: getExpertStatus, color: '#a78bfa', hints: ['扫描赛博朋克设定库...', '整合义体改造体系...', '构建霓虹废土世界观'] },
    { name: '白金大神作家', status: getWriterStatus, color: '#c084fc', hints: ['构思融合世界观...', '设计主角人设...', '撰写开篇章节'] },
    { name: '专业校对编辑', status: getReviewerStatus, color: '#e879f9', hints: ['校验设定一致性...', '检查情节逻辑...', '标注待修正项'] },
    { name: '文字润色大师', status: getReviewerStatus, color: '#f0abfc', hints: ['优化文风节奏...', '增强氛围描写...', '润色对话表达'] },
  ];

  const orchestratorActive = phase === 'assistant-reply1' || phase === 'assistant-reply2';

  return (
    <div className="w-full h-full rounded-lg overflow-hidden relative" style={{ background: 'linear-gradient(145deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)' }}>
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage: 'radial-gradient(circle, #a78bfa 0.8px, transparent 0.8px)',
          backgroundSize: '20px 20px',
        }}
      />

      <div className="relative z-10 h-full flex flex-col p-3 gap-2 overflow-hidden">
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
            <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/60" />
            <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
          </div>
          <span className="text-[9px] font-medium tracking-wider" style={{ color: '#64748b' }}>
            AGENT TEAM · ORCHESTRATOR
          </span>
          {phase !== 'idle' && phase !== 'reset' && (
            <div className="ml-auto flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-[8px] text-cyan-400/80">LIVE</span>
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-2 overflow-hidden">
          <div
            className={`flex justify-end transition-all duration-500 ${
              phase !== 'idle' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
            }`}
          >
            <div
              className="text-[11px] px-3.5 py-2 rounded-2xl rounded-tr-sm max-w-[85%]"
              style={{
                background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                color: '#f5f3ff',
                boxShadow: '0 2px 12px rgba(124,58,237,0.3)',
              }}
            >
              {userText}
              {phase === 'user-msg' && (
                <span className={`inline-block w-1.5 h-3 ml-0.5 align-middle ${showCursor ? 'bg-white/70' : 'bg-transparent'}`} />
              )}
            </div>
          </div>

          <div
            className={`flex justify-start transition-all duration-500 ${
              showAssistant1 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
            }`}
          >
            <div className="flex gap-2.5 max-w-[90%]">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-all duration-500 ${orchestratorActive ? 'animate-pulse' : ''}`}
                style={{
                  background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                  boxShadow: orchestratorActive ? '0 0 16px rgba(6,182,212,0.4)' : '0 0 8px rgba(6,182,212,0.2)',
                }}
              >
                <span className="text-[9px] text-white font-bold">助</span>
              </div>
              <div
                className="text-[11px] px-3.5 py-2 rounded-2xl rounded-tl-sm"
                style={{
                  background: 'rgba(30,41,59,0.6)',
                  border: '1px solid rgba(100,116,139,0.2)',
                  color: '#e2e8f0',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {assistantText1}
                {phase === 'assistant-reply1' && (
                  <span className={`inline-block w-1.5 h-3 ml-0.5 align-middle ${showCursor ? 'bg-cyan-400' : 'bg-transparent'}`} />
                )}
              </div>
            </div>
          </div>

          <div
            className={`flex justify-center transition-all duration-700 ${
              showCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="flex flex-col gap-1 w-[92%]">
              {agents.map((agent, i) => (
                <AgentNode
                  key={agent.name}
                  name={agent.name}
                  status={agent.status()}
                  delay={i * 120}
                  color={agent.color}
                  hints={agent.hints}
                />
              ))}
            </div>
          </div>

          <div
            className={`flex justify-start transition-all duration-500 ${
              showAssistant2 ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
            }`}
          >
            <div className="flex gap-2.5 max-w-[90%]">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                style={{
                  background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                  boxShadow: '0 0 16px rgba(6,182,212,0.4)',
                }}
              >
                <span className="text-[9px] text-white font-bold">助</span>
              </div>
              <div
                className="text-[11px] px-3.5 py-2 rounded-2xl rounded-tl-sm"
                style={{
                  background: 'rgba(30,41,59,0.6)',
                  border: '1px solid rgba(52,211,153,0.3)',
                  color: '#e2e8f0',
                  backdropFilter: 'blur(8px)',
                  boxShadow: '0 0 12px rgba(52,211,153,0.1)',
                }}
              >
                {assistantText2}
                {phase === 'assistant-reply2' && (
                  <span className={`inline-block w-1.5 h-3 ml-0.5 align-middle ${showCursor ? 'bg-emerald-400' : 'bg-transparent'}`} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ 左右交替布局行组件 ============
interface FeatureRowProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
  demo: React.ReactNode;
  reverse?: boolean;
}

const FeatureRow: React.FC<FeatureRowProps> = ({ icon, title, description, gradient, demo, reverse }) => (
  <div className={`flex flex-col ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-8 md:gap-16`}>
    {/* 动画演示区域 */}
    <div className="w-full md:w-1/2">
      <div className="relative rounded-2xl overflow-hidden shadow-sm bg-gradient-to-br from-gray-100 to-gray-50 border border-gray-200">
        <div className={`h-1 w-full bg-gradient-to-r ${gradient}`} />
        <div className="h-64 md:h-80 p-4">
          {demo}
        </div>
      </div>
    </div>

    {/* 文字介绍区域 */}
    <div className="w-full md:w-1/2 text-center md:text-left">
      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} shadow-sm mb-6`}>
        {icon}
      </div>
      <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        {title}
      </h3>
      <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
        {description}
      </p>
    </div>
  </div>
);

export const AgentShowcase: React.FC = () => {
  const { agentShowcase } = homePageContent;

  const features = [
    {
      icon: (
        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      gradient: 'from-paper-600 to-paper-400',
      demo: <AgentChatDemo />,
    },
    {
      icon: (
        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      gradient: 'from-paper-500 to-paper-300',
      demo: <WorkflowDemo />,
    },
    {
      icon: (
        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      gradient: 'from-paper-700 to-paper-500',
      demo: <AgentTeamsDemo />,
    },
  ];

  return (
    <section className="py-24 px-4 md:px-8 relative">

      <div className="max-w-6xl mx-auto">
        {/* 标题区域 */}
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-paper-100 text-paper-800 text-sm font-medium mb-4">
            {agentShowcase.badge}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {agentShowcase.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {agentShowcase.subtitle}
          </p>
        </div>

        {/* 功能行 - 左右交替布局 */}
        <div className="flex flex-col gap-24 md:gap-32">
          {agentShowcase.features.map((feature, index) => (
            <FeatureRow
              key={index}
              icon={features[index].icon}
              title={feature.title}
              description={feature.description}
              gradient={features[index].gradient}
              demo={features[index].demo}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
